import cx from 'classnames'

import { useCallback, useMemo, useState } from 'react'

import Styles from './ContentOptimization.module.scss'

import { Button } from 'blaze-ui/Button'
import { MultiStepStepLayout } from 'components/MultiStepFlow/MultiStepStepLayout/MultiStepStepLayout'
import { StickyFooterProvider } from 'providers/StickyFooterProvider/StickyFooterProvider'
import { FooterButtons, StickyFooter } from 'components/FirstTimeExperienceModal/components/Footer/Footer'
import { TopBar } from '../TopBar/TopBar'
import { ContentStrategyType } from 'types'
import { useUpdateBrandPlan } from 'hooks/queries/useUpdateBrandPlan'
import { useCurrentBrandPlan } from 'hooks/queries/brandPlans/useCurrentBrandPlan'
import dayjs from 'dayjs'
import { Strategy, buildRemainingContentStrategies } from '../StrategiesSelection/utils'
import { observer } from 'mobx-react-lite'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { EmojiMap } from 'components/ContentStrategyTypeEmoji/ContentStrategyTypeEmoji'
import { ContentStrategyTypeLabel } from '../../../constants'
import AddMore from 'blaze-ui/icons/24/AddMore'

export const ContentOptimization = observer(
  ({
    brandPlanId,
    brandPlanStrategies,
    onBack,
    onNext,
    highlightStep = 'goalsAndPlan',
  }: {
    brandPlanId: number
    brandPlanStrategies?: Strategy[]
    onBack: () => void
    onNext: () => void
    highlightStep?: string
  }) => {
    const { isMobile } = useResponsiveDesign()
    const [isProcessing, setIsProcessing] = useState(false)
    const { mutateAsync } = useUpdateBrandPlan({ id: brandPlanId })
    const { data: currentBrandPlan, refetch: refetchBrandPlan } = useCurrentBrandPlan()

    const contentStrategies = useMemo(() => {
      const remainingStrategies = brandPlanStrategies
        ? buildRemainingContentStrategies(brandPlanStrategies, 'aggressive')
        : {}

      return {
        ...currentBrandPlan?.content_strategies,
        ...remainingStrategies,
      }
    }, [currentBrandPlan?.content_strategies, brandPlanStrategies])

    const updateBrandPlan = useCallback(async () => {
      setIsProcessing(true)

      await mutateAsync({
        content_optimization_strategy: {
          strategy_growth: { type: 'aggressive', start_date: dayjs().format('YYYY-MM-DD') },
          frequency_growth: { type: 'competitive', start_date: dayjs().format('YYYY-MM-DD') },
        },
        content_strategies: contentStrategies,
      })
      await refetchBrandPlan()
      setIsProcessing(false)
      onNext()
    }, [mutateAsync, refetchBrandPlan, onNext, contentStrategies])

    return (
      <MultiStepStepLayout
        title="The more you use Blaze, the better your marketing gets."
        scrollContainerClassName={Styles.container}
      >
        <StickyFooterProvider
          footer={
            <StickyFooter>
              <FooterButtons>
                {!isMobile && (
                  <Button variant="tertiary" size="lg" onPress={onBack}>
                    Back
                  </Button>
                )}
                <Button size="lg" onPress={updateBrandPlan} isDisabled={isProcessing} fullWidth={isMobile}>
                  Next
                </Button>
              </FooterButtons>
            </StickyFooter>
          }
        >
          <TopBar highlightStep={highlightStep as React.ComponentProps<typeof TopBar>['highlightStep']} />
          <div className={Styles.content}>
            <div className={Styles.strategyGrowth}>
              <div className={Styles.header}>
                <Heading level={3}>The Learning Loop expands your strategy automatically.</Heading>
                <Text color="var(--dark-60)">
                  As Blaze learns what your audience responds to, it tests new content lanes for you—so you keep growing
                  without guessing what to try next.{' '}
                </Text>
              </div>
              <div className={Styles.body}>
                {Object.keys(contentStrategies)
                  .reverse()
                  .map((key, index) => (
                    <div className={cx(Styles.row, { [Styles.negativeTopMargin]: index !== 0 })} key={key}>
                      <div className={Styles.rowHeader}>
                        <Heading level={5}>{index === 3 ? 'Today' : `Week ${6 - index * 2}`}</Heading>
                      </div>
                      <div className={cx(index === 3 ? Styles.today : Styles[`week${6 - index * 2}`])}>
                        <Heading level={4} color="var(--light-100)">
                          {EmojiMap[key as ContentStrategyType]} {ContentStrategyTypeLabel[key as ContentStrategyType]}
                        </Heading>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
            <div className={Styles.postFrequencyGrowth}>
              <div className={Styles.header}>
                <Heading level={3}>The Learning Loop turns winning content into more wins.</Heading>
                <Text color="var(--dark-60)">
                  When a post performs well, Blaze creates more like it in the channels that matter—so your best ideas
                  keep compounding.
                </Text>
              </div>
              <div className={Styles.body}>
                <div className={Styles.thisWeek}>
                  <span className={Styles.impressions}>+300% impressions</span>
                  <img
                    src="https://res.cloudinary.com/almanac/image/upload/v1773880162/onboarding_assets/content_optimization_step/post_frequency_this_week_xnn953.png"
                    alt="This week"
                  />
                  <Heading level={5}>This Week</Heading>
                </div>
                <div className={Styles.nextWeek}>
                  <div className={Styles.additionalPosts}>
                    <AddMore />
                    <Heading level={5} color="var(--green)">
                      2 New posts
                    </Heading>
                  </div>

                  <img
                    src="https://res.cloudinary.com/almanac/image/upload/v1773881203/onboarding_assets/content_optimization_step/post_frequency_next_week_tfau6g.png"
                    alt="Next week"
                  />
                  <Heading level={5}>Next Week</Heading>
                </div>
              </div>
            </div>
          </div>
        </StickyFooterProvider>
      </MultiStepStepLayout>
    )
  },
)
